if (!customElements.get("product-gallery-lightbox")) {
  customElements.define(
    "product-gallery-lightbox",
    class ProductGalleryLightbox extends HTMLElement {
      connectedCallback() {
        // Se mueve al <body> para que ningún contexto de apilamiento de la sección lo tape.
        // Al moverlo se vuelve a llamar a connectedCallback, que es donde se inicializa.
        if (this.parentElement !== document.body) {
          document
            .querySelectorAll(`body > #${this.id}`)
            .forEach((oldLightbox) => oldLightbox.remove())
          document.body.appendChild(this)
          return
        }

        this.productInfo = document.getElementById(
          `MainProduct-${this.dataset.section}`
        )
        if (!this.productInfo) return

        this.track = this.querySelector("[data-track]")
        this.counter = this.querySelector("[data-counter]")
        this.slides = []
        this.index = 0

        this.onGalleryClick = this.onGalleryClick.bind(this)
        this.onKeydown = this.onKeydown.bind(this)
        this.onSwipeStart = this.onSwipeStart.bind(this)
        this.onSwipeEnd = this.onSwipeEnd.bind(this)

        this.productInfo.addEventListener("click", this.onGalleryClick)
        this.querySelector("[data-close]").addEventListener("click", () =>
          this.close()
        )
        this.querySelector("[data-prev]").addEventListener("click", () =>
          this.goTo(this.index - 1)
        )
        this.querySelector("[data-next]").addEventListener("click", () =>
          this.goTo(this.index + 1)
        )
        // Swipe horizontal para pasar de imagen: dedo (touch) o ratón (pointer de tipo mouse)
        this.track.addEventListener("touchstart", this.onSwipeStart, { passive: true })
        this.track.addEventListener("touchend", this.onSwipeEnd)
        this.track.addEventListener("pointerdown", this.onSwipeStart)
        this.addEventListener("pointerup", this.onSwipeEnd)
        // Escritorio: clic en la imagen = zoom; con zoom, el ratón recorre la imagen y otro clic lo quita.
        // Clic en el fondo (fuera de la imagen) cierra, salvo si viene de un swipe
        this.zoomMedia = window.matchMedia("(min-width: 768px) and (pointer: fine)")
        this.track.addEventListener("click", (event) => {
          if (this.justSwiped) {
            this.justSwiped = false
            return
          }
          if (this.zoomedSlide) {
            this.unzoom()
            return
          }
          if (this.zoomMedia.matches && event.target.matches(".gallery-lightbox__slide.is-active img")) {
            this.zoom(event)
            return
          }
          if (event.target === this.track || event.target.matches(".gallery-lightbox__slide")) {
            this.close()
          }
        })
        this.track.addEventListener("mousemove", (event) => {
          if (this.zoomedSlide) this.panTo(event)
        })

        // Al cambiar de color/variante desde la barra, se recarga con la galería visible
        this.variantUnsubscriber = FoxTheme.pubsub.subscribe(
          FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange,
          (event) => {
            if (this.hidden || event.data.sectionId !== this.dataset.section) return
            setTimeout(() => {
              const gallery = this.productInfo.querySelector(
                "media-gallery:not(.is-hidden)"
              )
              if (gallery) this.build(gallery, 0)
            }, 150)
          }
        )
      }

      disconnectedCallback() {
        if (!this.productInfo) return
        this.productInfo.removeEventListener("click", this.onGalleryClick)
        document.removeEventListener("keydown", this.onKeydown)
        if (this.variantUnsubscriber) this.variantUnsubscriber()
        this.unlockPage()
      }

      onGalleryClick(event) {
        if (event.target.closest("button, a, product-model, video-element")) return

        const item = event.target.closest(
          '.product__media-item[data-media-type="image"]'
        )
        if (!item) return

        const gallery = item.closest("media-gallery")
        if (!gallery || gallery.dataset.mediaLayout !== "featured-scroll") return

        this.lastFocused = item
        this.open(gallery, item)
      }

      // En móvil la galería es un swiper en bucle: se quitan duplicados y se ordena por posición original
      getImageItems(gallery) {
        const seen = new Set()
        return [
          ...gallery.querySelectorAll(
            '.product__media-item[data-media-type="image"]:not(.swiper-slide-duplicate)'
          ),
        ]
          .filter((item) => {
            if (seen.has(item.dataset.mediaId)) return false
            seen.add(item.dataset.mediaId)
            return true
          })
          .sort((a, b) => Number(a.dataset.mediaIndex) - Number(b.dataset.mediaIndex))
      }

      build(gallery, startIndex) {
        this.track.innerHTML = ""
        this.slides = this.getImageItems(gallery).map((mediaItem) => {
          const source = mediaItem.querySelector("img")
          const slide = document.createElement("div")
          slide.className = "gallery-lightbox__slide"

          const img = document.createElement("img")
          if (source && source.srcset) {
            img.srcset = source.srcset
            img.sizes = "(max-width: 767px) 100vw, 100vh"
          }
          img.src = mediaItem.dataset.src
          if (mediaItem.dataset.zoomSrc) img.dataset.zoomSrc = mediaItem.dataset.zoomSrc
          img.alt = (source && source.alt) || ""
          img.decoding = "async"
          img.draggable = false // si no, el ratón arrastra la imagen en vez de hacer swipe

          slide.appendChild(img)
          this.track.appendChild(slide)
          return slide
        })

        this.toggleAttribute("data-single", this.slides.length < 2)
        this.goTo(startIndex)
      }

      goTo(index) {
        const total = this.slides.length
        if (!total) return

        this.unzoom()
        this.index = (index + total) % total
        this.slides.forEach((slide, i) =>
          slide.classList.toggle("is-active", i === this.index)
        )
        this.counter.textContent = `${this.index + 1} / ${total}`
      }

      open(gallery, item) {
        const startIndex = Math.max(
          this.getImageItems(gallery).findIndex(
            (mediaItem) => mediaItem.dataset.mediaId === item.dataset.mediaId
          ),
          0
        )
        this.build(gallery, startIndex)

        this.hidden = false
        this.lockPage()
        document.addEventListener("keydown", this.onKeydown)
        this.querySelector("[data-close]").focus()
      }

      close() {
        if (this.hidden) return
        this.unzoom()
        this.hidden = true
        this.unlockPage()
        document.removeEventListener("keydown", this.onKeydown)
        if (this.lastFocused) this.lastFocused.focus({ preventScroll: true })
      }

      // La barra sticky solo abre sus desplegables (talla/color) con la clase --show
      lockPage() {
        document.documentElement.classList.add("gallery-lightbox-open")
        this.atcBar = document.querySelector(".sticky-atc-bar")
        if (this.atcBar) {
          this.atcBarWasShown = this.atcBar.classList.contains("sticky-atc-bar--show")
          this.atcBar.classList.add("sticky-atc-bar--show")
        }
      }

      unlockPage() {
        document.documentElement.classList.remove("gallery-lightbox-open")
        if (this.atcBar && !this.atcBarWasShown) {
          this.atcBar.classList.remove("sticky-atc-bar--show")
        }
        this.atcBar = null
      }

      zoom(event) {
        const slide = this.slides[this.index]
        const img = slide.querySelector("img")
        this.zoomedSlide = slide
        this.panTo(event)
        slide.classList.add("is-zoomed")

        // Versión de más resolución; se cambia cuando ya está cargada para que no parpadee
        if (img.dataset.zoomSrc && !img.dataset.zoomLoaded) {
          img.dataset.zoomLoaded = "true"
          const hiRes = new Image()
          hiRes.onload = () => {
            img.removeAttribute("srcset")
            img.removeAttribute("sizes")
            img.src = hiRes.src
          }
          hiRes.src = img.dataset.zoomSrc
        }
      }

      unzoom() {
        if (!this.zoomedSlide) return
        this.zoomedSlide.classList.remove("is-zoomed")
        this.zoomedSlide = null
      }

      // La posición del ratón dentro de la zona de la imagen decide qué parte se ve ampliada
      panTo(event) {
        const rect = this.zoomedSlide.getBoundingClientRect()
        const clamp = (value) => Math.min(Math.max(value, 0), 100)
        const x = clamp(((event.clientX - rect.left) / rect.width) * 100)
        const y = clamp(((event.clientY - rect.top) / rect.height) * 100)
        this.zoomedSlide.style.setProperty("--zoom-origin", `${x}% ${y}%`)
      }

      // Con el dedo llegan touch y pointer a la vez: de pointer solo se usa el ratón
      getSwipePoint(event) {
        if (event.changedTouches) return event.changedTouches[0]
        return event.pointerType === "mouse" ? event : null
      }

      onSwipeStart(event) {
        const point = this.getSwipePoint(event)
        if (!point || this.zoomedSlide) return // con zoom, el ratón mueve la imagen, no pasa de foto
        this.justSwiped = false
        this.swipeStart = { x: point.clientX, y: point.clientY }
      }

      onSwipeEnd(event) {
        const point = this.getSwipePoint(event)
        if (!point || !this.swipeStart) return
        const deltaX = point.clientX - this.swipeStart.x
        const deltaY = point.clientY - this.swipeStart.y
        this.swipeStart = null
        if (Math.abs(deltaX) < 40 || Math.abs(deltaX) < Math.abs(deltaY)) return

        this.justSwiped = true // el clic que sigue al swipe no debe cerrar el carrusel
        this.goTo(this.index + (deltaX < 0 ? 1 : -1))
      }

      onKeydown(event) {
        switch (event.key) {
          case "Escape":
            if (this.zoomedSlide) this.unzoom()
            else this.close()
            break
          case "ArrowLeft":
            this.goTo(this.index - 1)
            break
          case "ArrowRight":
            this.goTo(this.index + 1)
            break
        }
      }
    }
  )
}
